import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Project } from "@/types/projectType";
import { getImageUrl } from "@/utils/image";
import { truncateText } from "@/utils/text";

interface ProjectCardProps {
  project: Project;
  onClick?: (project: Project) => void;
}

/**
 * ProjectCard Component
 * Displays a single project's information including image, name, and content preview
 */
const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(project);
    }
  };

  const cardContent = (
    <div
      className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
        onClick ? "cursor-pointer hover:scale-[1.02] transition-transform" : ""
      }`}
      onClick={handleClick}
    >
      {/* Project Image */}
      <div className="relative h-48 sm:h-56 bg-gray-100">
        {project.img?.url ? (
          <Image
            src={getImageUrl(project.img.url)}
            alt={project.img.alt || project.name}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-100 to-blue-100">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-2 bg-green-500 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">Dự án</p>
            </div>
          </div>
        )}

        {/* Overlay gradient for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
      </div>

      {/* Project Content */}
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
          {project.name}
        </h3>

        <div className="text-gray-600 text-sm leading-relaxed mb-4">
          <div
            dangerouslySetInnerHTML={{
              __html: truncateText(
                typeof project.content === "string"
                  ? JSON.parse(project.content).html
                  : project.content.html,
                150
              ),
            }}
          />
        </div>

        {/* Project Meta */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <time dateTime={project.createdAt}>
            {new Date(project.createdAt).toLocaleDateString("vi-VN", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </time>

          {onClick && (
            <span className="text-green-600 hover:text-green-700 font-medium">
              Xem chi tiết →
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Link
      href={`/project/${project.id}`}
      className="hover:no-underline"
    >
      {cardContent}
    </Link>
  );
};

export default ProjectCard;
